import { Checkbox, CheckboxGroup, VStack } from "@chakra-ui/react"
import FieldCheckboxGroup from "@pathwright/ui/src/components/form/form-checkbox/CheckboxGroup"
import LoadingBlock from "@pathwright/ui/src/components/loading/LoadingBlock"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import get from "lodash/get"
import PropTypes from "prop-types"
import styled from "styled-components"
import { useSchoolSubscriptionPlansQuery } from "@pathwright/graphql-client/src/generated"
import { useCurrencyFormatter } from "../../currency/CurrencyFormatterProvider"
import { flattenEdges } from "../../utils/apollo"

const StyledCheckboxGroup = styled(FieldCheckboxGroup)`
  /* white-space: nowrap; */
  overflow: hidden;
  text-overflow: ellipsis;

  & > div {
    align-items: flex-start;
  }
`

const StyledPlanDetails = styled.span`
  position: relative;
  display: grid;
  ${(p) =>
    p.locked
      ? `grid-template-columns: max-content max-content 1fr max-content;`
      : `grid-template-columns: max-content 1fr max-content;`}
  align-items: center;

  img {
    height: 1em;
    padding-right: 5px;
    padding-left: 5px;
  }

  .Pathicon {
    margin-left: 5px;
  }
`

export const useSchoolSubscriptionPlans = ({
  interval,
  intervalFilter
} = {}) => {
  const { data, error, loading, refetch } = useSchoolSubscriptionPlansQuery({
    variables: {
      ignore_visibility: true,
      interval,
      intervalFilter
    }
  })

  return {
    schoolSubscriptionPlans:
      flattenEdges(get(data, "school.subscription_plans")) || [],
    error,
    loading,
    refetch
  }
}

const schoolSubscriptionPlansCheckboxesDefaultPropValue = []

const SchoolSubscriptionPlansCheckboxes = ({
  onChange,
  value = schoolSubscriptionPlansCheckboxesDefaultPropValue,
  interval,
  intervalFilter,
  ...rest
}) => {
  const { currencyFormatter } = useCurrencyFormatter()
  const { loading, schoolSubscriptionPlans } = useSchoolSubscriptionPlans({
    interval,
    intervalFilter
  })

  if (loading) {
    return <LoadingBlock />
  }

  if (!schoolSubscriptionPlans.length) {
    return null
  }

  const handleValueChange = (stringIds) => {
    const ids = stringIds.map((id) => parseInt(id, 10))
    const plans = schoolSubscriptionPlans.filter((plan) =>
      ids.includes(plan.id)
    )

    if (onChange) onChange(ids, plans)
  }

  // Sort by interval, then by amount, then by name, then by visibility
  const sortedPlans = schoolSubscriptionPlans
    .sort((a, b) => a.subscription_plan.interval - b.subscription_plan.interval)
    .sort((a, b) => a.subscription_plan.amount - b.subscription_plan.amount)
    .sort((a, b) => {
      const nameA = a.subscription_plan.name.toUpperCase()
      const nameB = b.subscription_plan.name.toUpperCase()
      return nameA < nameB ? -1 : nameA > nameB ? 1 : 0
    })
    .sort((a, b) =>
      a.is_visible !== b.is_visible ? (a.is_visible ? -1 : 1) : 0
    )

  return (
    <StyledCheckboxGroup {...rest}>
      <CheckboxGroup
        value={(value || []).map(String)}
        onValueChange={handleValueChange}
      >
        <VStack align="stretch" gap={1}>
          {sortedPlans.map((plan) => (
            <Checkbox.Root
              key={plan.id}
              value={String(plan.id)}
              colorPalette="blue"
              variant="solid"
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
              <Checkbox.Label>
                <StyledPlanDetails locked={!plan.is_visible}>
                  {!plan.is_visible && <Pathicon icon="lock" />}
                  <img src={plan.image} />
                  <strong>{plan.subscription_plan.name}</strong>
                  &#8212;
                  {currencyFormatter.customFormat(
                    plan.subscription_plan.amount
                  )}
                  /{plan.subscription_plan.plan_type}
                </StyledPlanDetails>
              </Checkbox.Label>
            </Checkbox.Root>
          ))}
        </VStack>
      </CheckboxGroup>
    </StyledCheckboxGroup>
  )
}

SchoolSubscriptionPlansCheckboxes.displayName =
  "SchoolSubscriptionPlansCheckboxes"

SchoolSubscriptionPlansCheckboxes.propTypes = {
  value: PropTypes.array, // list of selected school subscription plan ids - this is a controlled component
  onChange: PropTypes.func
}

export default SchoolSubscriptionPlansCheckboxes
